import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";

import { OnboardingState, ProspectDetail } from "../../../../types/onboarding";
import {
  canGenerateContractDraft,
  isDraftCreated,
  isEditableState,
  isTerminal,
  nextPipelineStates,
  stateLabel,
} from "../../../../utils/onboardingStates";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import AdvanceStateDialog from "./AdvanceStateDialog";
import MoreInfoDialog from "./MoreInfoDialog";
import ResignDialog from "./ResignDialog";
import EditProspectDialog from "./EditProspectDialog";
import AssignTechnicalNamesDialog from "./AssignTechnicalNamesDialog";
import SetupFeeDialog from "./SetupFeeDialog";
import { stationCounts } from "../../../../utils/stationCounts";
import { isCustomerPromoted } from "../../../../utils/setupFee";
import { UseProspectSetupFee } from "../../../../hooks/useProspectSetupFee";

interface StatusActionsBarProps {
  prospect: ProspectDetail;
  detail: {
    setState: (
      payload: {
        target_state: OnboardingState;
        more_info_reason?: string | null;
        resign_reason?: string | null;
        resign_note?: string | null;
      },
      successMessage: string
    ) => Promise<void>;
    saveEdit: (payload: any) => Promise<boolean>;
    assignNames: (customNames?: string[]) => Promise<boolean>;
    generateDraft: () => Promise<boolean>;
    // True while a draft is being generated in the background (CWA-187),
    // including one started before this page was opened.
    draftGenerating?: boolean;
  };
  // Set when the next-step button asks for one of the actions this bar owns.
  requestedAction?: NextStepAction | null;
  onRequestHandled?: () => void;
  // True while an action STARTED FROM THE NEXT-STEP BUTTON is running, so the
  // spinner can appear on the button that was actually pressed.
  onBusyChange?: (busy: boolean) => void;
  // The onboarding setup fee (CWA-196). Optional: absent, the item is not offered.
  setupFee?: UseProspectSetupFee;
}

type OpenDialog =
  | "none"
  | "advance"
  | "moreInfo"
  | "resign"
  | "edit"
  | "technicalNames"
  | "setupFee";

/**
 * The prospect's supporting actions (US2 + US3 entry points), behind one menu.
 *
 * These were five buttons across the top of the screen, with the stage override
 * filled in the primary colour — so the loudest control on the page was the one
 * that skips the process, while the step that actually advances the customer sat
 * in a panel below. They are corrections and overrides, not the normal route, so
 * they now read as a menu beside the next-step button rather than competing with
 * it. A menu, not a tab of their own: an action is not content, and putting a
 * resign behind a tab switch hides it from the agent who needs it while adding a
 * click for everyone else.
 *
 * Two of them — assigning the station IDs and generating the draft — ARE the
 * next step at their stage, so the header's button drives them through
 * `requestedAction` instead of duplicating their dialogs.
 *
 * Status actions are hidden once the prospect is terminally resigned
 * (FR-013/FR-014); editing is hidden once the prospect is past the edit-lock
 * state (FR-016).
 */
const StatusActionsBar: React.FC<StatusActionsBarProps> = ({
  prospect,
  detail,
  requestedAction = null,
  onRequestHandled,
  onBusyChange,
  setupFee,
}) => {
  const [open, setOpen] = useState<OpenDialog>("none");
  // Which control started the draft generation, not merely that one did: the
  // work is the same, but the waiting belongs on the button the agent pressed.
  const [generatingFrom, setGeneratingFrom] = useState<"menu" | "next-step" | null>(
    null
  );
  const menu = useRef<Menu>(null);
  const close = () => setOpen("none");

  const terminal = isTerminal(prospect.current_state);
  const canAdvance =
    !terminal && nextPipelineStates(prospect.current_state).length > 0;
  const editable = isEditableState(prospect.current_state);

  // Contract draft: offered once stations are assigned; "Re-generate" once a
  // draft already exists; never for terminal prospects.
  const canGenerateDraft = canGenerateContractDraft(prospect.current_state);
  const draftExists = isDraftCreated(prospect.current_state);
  const showDraftAction = !terminal && (canGenerateDraft || draftExists);
  // A draft generating in the background that this page didn't start (it was
  // opened or reloaded mid-generation): show the wait where it would have been
  // started — the next-step button while the draft is the next step, else the menu.
  const resumedDraft = generatingFrom === null && !!detail.draftGenerating;
  const draftBusy = generatingFrom !== null || !!detail.draftGenerating;
  const menuWaiting =
    generatingFrom === "menu" || (resumedDraft && !canGenerateDraft);

  const onGenerateDraft = async (from: "menu" | "next-step") => {
    setGeneratingFrom(from);
    try {
      await detail.generateDraft();
    } finally {
      setGeneratingFrom(null);
    }
  };

  useEffect(() => {
    onBusyChange?.(
      generatingFrom === "next-step" || (resumedDraft && canGenerateDraft)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingFrom, resumedDraft, canGenerateDraft]);

  // Runs once per request from the next-step button, then hands the request
  // back: re-running on every render would re-open the dialog an agent just
  // closed.
  useEffect(() => {
    if (!requestedAction) return;
    if (requestedAction === "assign-station-ids") setOpen("technicalNames");
    if (requestedAction === "generate-draft") onGenerateDraft("next-step");
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  const items: MenuItem[] = [];
  if (!terminal) {
    if (editable) {
      items.push({
        label: "Edit prospect",
        icon: "pi pi-pencil",
        command: () => setOpen("edit"),
      });
    }
    items.push({
      label: "Assign station IDs",
      icon: "pi pi-tag",
      command: () => setOpen("technicalNames"),
    });
    if (showDraftAction) {
      items.push({
        label: draftBusy
          ? "Generating contract draft…"
          : draftExists
          ? "Re-generate contract draft"
          : "Generate contract draft",
        icon: draftBusy ? "pi pi-spin pi-spinner" : "pi pi-file-edit",
        disabled: draftBusy,
        command: () => onGenerateDraft("menu"),
      });
    }
    // Existing customers are invoiced from here (CWA-196 US2). A pending
    // invoice stays reachable, since its retry is the only way out of it.
    const fee = setupFee?.setupFee;
    if (
      fee &&
      isCustomerPromoted(prospect.current_state) &&
      (fee.status === "OPEN" || fee.status === "INVOICING")
    ) {
      items.push({
        label: "Setup fee…",
        icon: "pi pi-euro",
        command: () => setOpen("setupFee"),
      });
    }
    if (canAdvance) {
      items.push({ separator: true });
      items.push({
        // Named for what it is: a hand-set stage, not the way the pipeline is
        // meant to move.
        label: "Set status manually",
        icon: "pi pi-arrow-right",
        command: () => setOpen("advance"),
      });
    }
    items.push({
      label: "Mark: more info needed",
      icon: "pi pi-flag",
      command: () => setOpen("moreInfo"),
    });
    items.push({ separator: true });
    items.push({
      label: "Resign prospect",
      icon: "pi pi-times-circle",
      className: "prospect-menu-danger",
      command: () => setOpen("resign"),
    });
  }

  return (
    <>
      {!terminal && (
        <>
          <Menu
            model={items}
            popup
            ref={menu}
            id="prospect-actions-menu"
            className="prospect-actions-menu"
            appendTo={typeof document !== "undefined" ? document.body : undefined}
          />
          <Button
            label="More actions"
            // The draft is generated in the background (CWA-187) and can take a
            // while, so the wait is a spinner rather than `loading`: that would
            // disable the button and lock every other action in the menu.
            icon={menuWaiting ? "pi pi-spin pi-spinner" : "pi pi-ellipsis-h"}
            severity="secondary"
            outlined
            size="small"
            aria-haspopup
            aria-controls="prospect-actions-menu"
            aria-busy={menuWaiting}
            onClick={(e) => menu.current?.toggle(e)}
          />
        </>
      )}

      <AdvanceStateDialog
        visible={open === "advance"}
        currentState={prospect.current_state}
        onHide={close}
        onConfirm={(target) =>
          detail.setState(
            { target_state: target },
            `Status advanced to "${stateLabel(target)}".`
          )
        }
      />

      <MoreInfoDialog
        visible={open === "moreInfo"}
        onHide={close}
        onConfirm={(reason) =>
          detail.setState(
            { target_state: "MORE_INFO_NEEDED", more_info_reason: reason },
            "Flagged as needing more info."
          )
        }
      />

      <ResignDialog
        visible={open === "resign"}
        onHide={close}
        onConfirm={(reasonCode, note) =>
          detail.setState(
            {
              target_state: "RESIGNED",
              resign_reason: reasonCode,
              resign_note: note || null,
            },
            "Prospect resigned."
          )
        }
      />

      <EditProspectDialog
        visible={open === "edit"}
        prospect={prospect}
        onHide={close}
        onSave={detail.saveEdit}
      />

      {setupFee?.setupFee && (
        <SetupFeeDialog
          visible={open === "setupFee"}
          setupFee={setupFee.setupFee}
          onHide={close}
          onInvoice={setupFee.invoice}
          onWaive={setupFee.waive}
        />
      )}

      <AssignTechnicalNamesDialog
        visible={open === "technicalNames"}
        // One station ID per WALLBOX, not per charging point: eRound registers
        // the wallbox and a two-socket box needs one technical name, not two
        // (CWA-154 finding 1).
        stationCount={stationCounts(prospect).stations}
        existingNames={prospect.technical_names}
        onHide={close}
        onAssign={detail.assignNames}
      />
    </>
  );
};

export default StatusActionsBar;
